if (!customElements.get('volume-quantity-bar')) {
  class VolumeQuantityBar extends HTMLElement {
    constructor() {
      super();
      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.container = document.getElementById('ab-volume-discount-bar');
      this.section = this.closest('.js-product');
      this.form = this.section?.querySelector('.js-product-form-main');
      this.priceContainer = this.section?.querySelector('.product-info__price > .price');
      this.options = this.querySelectorAll('.volume-quantity-bar__option');
      this.config = this.getConfig();

      this.options.forEach((option) => {
        option.addEventListener('click', this.handleOptionClick);
      });

      if (this.section) {
        this.section.addEventListener('on:variant:change', this.handleVariantChange);
      }

      if (this.container) {
        this.observer = new MutationObserver(this.handleVisibilityChange);
        this.observer.observe(this.container, {
          attributes: true,
          attributeFilter: ['class', 'data-preview', 'style']
        });
      }

      window.addEventListener('load', () => this.syncVisibilityState());
      this.syncVisibilityState();
    }

    disconnectedCallback() {
      this.options.forEach((option) => {
        option.removeEventListener('click', this.handleOptionClick);
      });

      if (this.section) {
        this.section.removeEventListener('on:variant:change', this.handleVariantChange);
      }

      if (this.observer) this.observer.disconnect();
    }

    getConfig() {
      const script = this.querySelector('[data-volume-config]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Volume quantity bar config could not be parsed.', error); // eslint-disable-line
        return null;
      }
    }

    isActive() {
      if (!this.container) return false;
      if (this.container.dataset.preview === 'true') return true;
      if (this.container.classList.contains('is-visible')) return true;
      return window.getComputedStyle(this.container).display !== 'none';
    }

    handleVisibilityChange() {
      this.syncVisibilityState();
    }

    syncVisibilityState() {
      if (!this.form) return;

      if (this.isActive()) {
        this.syncQuantityInput();
        this.updatePrices();
      } else {
        this.restorePrices();
      }
    }

    handleVariantChange(event) {
      if (!this.isActive()) return;

      const variant = event.detail?.variant;
      if (variant) {
        this.currentVariantId = variant.id;
        this.currentVariantPrice = variant.price;
        this.currentCompareAtPrice = variant.compare_at_price;
      }

      this.updatePrices();
    }

    handleOptionClick(event) {
      const option = event.currentTarget;
      if (option.classList.contains('is-selected')) return;

      this.options.forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-checked', 'false');
      });
      option.classList.add('is-selected');
      option.setAttribute('aria-checked', 'true');

      this.selectedQuantity = Number(option.dataset.quantity);
      this.selectedDiscount = Number(option.dataset.discount || 0);

      this.syncQuantityInput();
      this.updatePrices();
      this.syncStickyQuantity();
    }

    getSelectedOption() {
      return this.querySelector('.volume-quantity-bar__option.is-selected')
        || this.options[0];
    }

    getVariantPriceCents() {
      if (this.currentVariantPrice) return this.currentVariantPrice;

      const variantInput = this.form?.querySelector('[name="id"]');
      const variantId = variantInput?.value;

      if (this.config?.variants?.[variantId]) {
        const variant = this.config.variants[variantId];
        this.currentVariantId = variant.id;
        this.currentVariantPrice = variant.price;
        this.currentCompareAtPrice = variant.compare_at_price;
        return variant.price;
      }

      const variantPicker = this.section?.querySelector('variant-picker');

      if (variantPicker && variantId) {
        try {
          const data = JSON.parse(
            variantPicker.querySelector('script[type="application/json"]').textContent
          );
          const variant = data.product.variants.find(
            (item) => String(item.id) === String(variantId)
          );
          if (variant) {
            this.currentVariantId = variant.id;
            this.currentVariantPrice = variant.price;
            this.currentCompareAtPrice = variant.compare_at_price;
            return variant.price;
          }
        } catch (error) {
          // fall through
        }
      }

      return Number(this.config?.basePrice || 0);
    }

    syncQuantityInput() {
      if (!this.form) return;

      const selected = this.getSelectedOption();
      const quantity = Number(selected?.dataset.quantity || 1);
      const qtyInput = this.form.querySelector('quantity-input input[name="quantity"]')
        || this.form.querySelector('input[name="quantity"]');

      if (qtyInput) qtyInput.value = quantity;
    }

    formatMoney(cents) {
      const format = this.config?.moneyFormat || '{{amount}} €';
      let amount = (Number(cents) / 100).toFixed(2);
      const amountWithCommaSeparator = amount.replace('.', ',');
      const amountNoDecimals = Math.round(Number(cents) / 100).toString();
      const amountWithApostropheSeparator = amountNoDecimals.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        "'"
      );

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithCommaSeparator)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountWithCommaSeparator.split(',')[0])
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountWithApostropheSeparator)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, amount.replace('.', ','));
    }

    roundMoney(cents) {
      return Math.round(Number(cents));
    }

    updatePrices() {
      if (!this.isActive() || !this.priceContainer) return;

      const selected = this.getSelectedOption();
      const quantity = Number(selected?.dataset.quantity || 1);
      const discount = Number(selected?.dataset.discount || 0);
      const unitPriceCents = this.getVariantPriceCents();
      const discountedUnitCents = this.roundMoney(unitPriceCents * (1 - discount / 100));
      const totalCents = discountedUnitCents * quantity;
      const compareAtTotal = this.currentCompareAtPrice
        ? this.currentCompareAtPrice * quantity
        : null;

      const priceCurrentEl = this.priceContainer.querySelector('.price__current');
      const priceWasEl = this.priceContainer.querySelector('.price__was');
      const unitPriceEl = this.priceContainer.querySelector('.unit-price');

      if (priceCurrentEl) {
        priceCurrentEl.innerHTML = this.formatMoney(totalCents);
      }

      if (priceWasEl) {
        if (compareAtTotal && compareAtTotal > totalCents) {
          priceWasEl.innerHTML = this.formatMoney(compareAtTotal);
        } else if (discount > 0) {
          priceWasEl.innerHTML = this.formatMoney(unitPriceCents * quantity);
        } else {
          priceWasEl.innerHTML = '';
        }
      }

      this.priceContainer.classList.toggle(
        'price--on-sale',
        Boolean((compareAtTotal && compareAtTotal > totalCents) || discount > 0)
      );

      if (unitPriceEl) unitPriceEl.hidden = true;

      let volumeUnitPriceEl = this.priceContainer.querySelector('.volume-unit-price');
      if (!volumeUnitPriceEl) {
        volumeUnitPriceEl = document.createElement('span');
        volumeUnitPriceEl.className = 'volume-unit-price';
        this.priceContainer.querySelector('.price__default')?.appendChild(volumeUnitPriceEl);
      }

      const unitLabel = this.config?.unitLabel || 'Stück';
      volumeUnitPriceEl.textContent = ` ${this.formatMoney(discountedUnitCents)} / ${unitLabel} `;
      volumeUnitPriceEl.hidden = false;
    }

    restorePrices() {
      if (!this.priceContainer) return;

      const volumeUnitPriceEl = this.priceContainer.querySelector('.volume-unit-price');
      if (volumeUnitPriceEl) volumeUnitPriceEl.hidden = true;

      const variantPicker = this.section?.querySelector('variant-picker');
      if (variantPicker && typeof variantPicker.updatePrice === 'function') {
        variantPicker.updatePrice();
        return;
      }

      const priceCurrentEl = this.priceContainer.querySelector('.price__current');
      const priceWasEl = this.priceContainer.querySelector('.price__was');
      const variantInput = this.form?.querySelector('[name="id"]');
      const variantId = variantInput?.value;
      const variant = this.config?.variants?.[variantId];

      if (variant && priceCurrentEl) {
        priceCurrentEl.innerHTML = this.formatMoney(variant.price);
        if (priceWasEl) {
          priceWasEl.innerHTML = variant.compare_at_price > variant.price
            ? this.formatMoney(variant.compare_at_price)
            : '';
        }
        this.priceContainer.classList.toggle(
          'price--on-sale',
          variant.compare_at_price > variant.price
        );
      }
    }

    syncStickyQuantity() {
      const selected = this.getSelectedOption();
      const quantity = Number(selected?.dataset.quantity || 1);
      const stickyQty = document.querySelector('.sticky-atc-panel input[name="quantity"]');
      if (stickyQty) stickyQty.value = quantity;
    }
  }

  customElements.define('volume-quantity-bar', VolumeQuantityBar);
}
